import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import "../assets/css/style.css";

import { API_BASE_URL } from "../config/api";
import { ItemOrderControls, ItemOrderMessage, useItemOrdering } from "../components/ItemOrderControls";

const BASE_URL = API_BASE_URL;

const createEmptyEventForm = () => ({
  title: "",
  title_ar: "",
  date: "",
  content: "",
  content_ar: "",
});

const resolveMediaUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${BASE_URL}/${value}`;
};

const renderMediaPreview = (item, key) => {
  if (!item?.path) return null;

  if (item.media_type === "video") {
    return (
      <video
        key={key}
        src={resolveMediaUrl(item.path)}
        controls
        style={{ maxWidth: "120px", marginRight: "10px", marginBottom: "10px" }}
      />
    );
  }

  return (
    <img
      key={key}
      src={resolveMediaUrl(item.path)}
      alt={key}
      style={{ maxWidth: "120px", marginRight: "10px", marginBottom: "10px" }}
    />
  );
};

const moveArrayItem = (items, index, targetIndex) => {
  if (index < 0 || targetIndex < 0 || index >= items.length || targetIndex >= items.length) {
    return items;
  }
  const nextItems = [...items];
  [nextItems[index], nextItems[targetIndex]] = [nextItems[targetIndex], nextItems[index]];
  return nextItems;
};

const MediaOrderEditor = ({ title, englishItems = [], arabicItems = [], onMove }) => {
  const count = Math.max(englishItems.length, arabicItems.length);
  if (!count) return null;
  const canReorder = englishItems.length === arabicItems.length;

  const renderItem = (item, language, index) => {
    if (!item) return <span style={{ color: "#999" }}>Not selected</span>;
    if (item instanceof File) return <span>{item.name}</span>;
    return renderMediaPreview(item, `${language}-order-${item.id || index}`);
  };

  return (
    <div style={{ marginTop: "12px" }}>
      <strong>{title}</strong>
      {!canReorder && (
        <div style={{ color: "#b36b00", marginTop: "6px" }}>
          Select the same number of English and Arabic media files before reordering.
        </div>
      )}
      <div style={{ marginTop: "8px" }}>
        {Array.from({ length: count }, (_, index) => (
          <div
            key={`media-order-${index}-${englishItems[index]?.id || englishItems[index]?.name || index}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px",
              marginBottom: "8px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              background: "#fff",
            }}
          >
            <strong style={{ minWidth: "28px" }}>{index + 1}</strong>
            <div style={{ flex: 1 }}><small>EN</small><br />{renderItem(englishItems[index], "en", index)}</div>
            <div style={{ flex: 1 }}><small>AR</small><br />{renderItem(arabicItems[index], "ar", index)}</div>
            <div style={{ whiteSpace: "nowrap" }}>
              <Button type="button" size="sm" color="secondary" disabled={!canReorder || index === 0} onClick={() => onMove(index, -1)}>↑</Button>{" "}
              <Button type="button" size="sm" color="secondary" disabled={!canReorder || index === count - 1} onClick={() => onMove(index, 1)}>↓</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function Events() {
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState(createEmptyEventForm);
  const [newMainMediaEn, setNewMainMediaEn] = useState(null);
  const [newMainMediaAr, setNewMainMediaAr] = useState(null);
  const [newAdditionalMediaEn, setNewAdditionalMediaEn] = useState([]);
  const [newAdditionalMediaAr, setNewAdditionalMediaAr] = useState([]);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [updateEvent, setUpdateEvent] = useState({
    ...createEmptyEventForm(),
    main_image_en: "",
    main_media_type_en: "",
    main_image_ar: "",
    main_media_type_ar: "",
    additional_media_en: [],
    additional_media_ar: [],
  });
  const [updateMainMediaEn, setUpdateMainMediaEn] = useState(null);
  const [updateMainMediaAr, setUpdateMainMediaAr] = useState(null);
  const [updateAdditionalMediaEn, setUpdateAdditionalMediaEn] = useState([]);
  const [updateAdditionalMediaAr, setUpdateAdditionalMediaAr] = useState([]);
  const [eventCount, setEventCount] = useState(0);
  const [addError, setAddError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [addSuccessMessage, setAddSuccessMessage] = useState("");
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState("");
  const [requiredError, setRequiredError] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const { moveItem, orderMessage, savingOrder } = useItemOrdering(events, setEvents, `${BASE_URL}/event/order`);

  const isValidMediaExtension = (fileName) => {
    if (!fileName) return false;
    const validExtensions = [".webp", ".gif", ".png", ".jpg", ".jpeg", ".mp4", ".mov", ".avi", ".mkv", ".webm"];
    const fileExtension = fileName
      .slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)
      .toLowerCase();
    return validExtensions.includes(`.${fileExtension}`);
  };

  const validateMediaFiles = (files = []) => files.every((file) => isValidMediaExtension(file.name || ""));

  const moveNewMediaPair = (index, direction) => {
    const targetIndex = index + direction;
    setNewAdditionalMediaEn((items) => moveArrayItem(items, index, targetIndex));
    setNewAdditionalMediaAr((items) => moveArrayItem(items, index, targetIndex));
  };

  const moveReplacementMediaPair = (index, direction) => {
    const targetIndex = index + direction;
    setUpdateAdditionalMediaEn((items) => moveArrayItem(items, index, targetIndex));
    setUpdateAdditionalMediaAr((items) => moveArrayItem(items, index, targetIndex));
  };

  const moveExistingMediaPair = (index, direction) => {
    const targetIndex = index + direction;
    setUpdateEvent((current) => ({
      ...current,
      additional_media_en: moveArrayItem(current.additional_media_en || [], index, targetIndex),
      additional_media_ar: moveArrayItem(current.additional_media_ar || [], index, targetIndex),
    }));
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/event/list`);
      const nextEvents = Array.isArray(response.data?.data) ? response.data.data : [];
      setEvents(nextEvents);
      setEventCount(nextEvents.length);
    } catch (error) {
      console.error(`Error getting data: ${error}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (showUpdateForm) {
      const titleInputField = document.getElementById("edit-event-title");
      if (titleInputField) {
        titleInputField.focus();
      }
    }
  }, [showUpdateForm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (showUpdateForm) {
      setUpdateEvent((prev) => ({ ...prev, [name]: value }));
    } else {
      setNewEvent((prev) => ({ ...prev, [name]: value }));
    }
  };

  const resetAddMediaInputs = () => {
    const ids = [
      "newEventMainMediaEn",
      "newEventMainMediaAr",
      "newEventAdditionalMediaEn",
      "newEventAdditionalMediaAr",
    ];
    ids.forEach((id) => {
      const input = document.getElementById(id);
      if (input) input.value = "";
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRequiredError("");
    setAddError("");

    if (
      !newEvent.title ||
      !newEvent.title_ar ||
      !newEvent.date ||
      !newEvent.content ||
      !newEvent.content_ar ||
      !newMainMediaEn ||
      !newMainMediaAr
    ) {
      setRequiredError("All fields are required, including both main media files.");
      return;
    }

    if (!isValidMediaExtension(newMainMediaEn.name) || !isValidMediaExtension(newMainMediaAr.name)) {
      setAddError("Invalid main media file extension. Please upload image/video formats.");
      return;
    }

    if (!validateMediaFiles(newAdditionalMediaEn) || !validateMediaFiles(newAdditionalMediaAr)) {
      setAddError("Invalid file extension in additional media. Please upload image/video formats.");
      return;
    }

    if (newAdditionalMediaEn.length !== newAdditionalMediaAr.length) {
      setAddError("English and Arabic additional media must have the same count.");
      return;
    }

    const formData = new FormData();
    formData.append("title", newEvent.title);
    formData.append("title_ar", newEvent.title_ar);
    formData.append("date", newEvent.date);
    formData.append("content", newEvent.content);
    formData.append("content_ar", newEvent.content_ar);
    formData.append("main_image_en", newMainMediaEn);
    formData.append("main_image_ar", newMainMediaAr);
    newAdditionalMediaEn.forEach((file) => formData.append("image_paths_en", file));
    newAdditionalMediaAr.forEach((file) => formData.append("image_paths_ar", file));

    try {
      await axios.post(`${BASE_URL}/event/add`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchData();
      setAddSuccessMessage("Event added successfully");
      setTimeout(() => setAddSuccessMessage(""), 5000);
      setNewEvent(createEmptyEventForm());
      setNewMainMediaEn(null);
      setNewMainMediaAr(null);
      setNewAdditionalMediaEn([]);
      setNewAdditionalMediaAr([]);
      resetAddMediaInputs();
    } catch (error) {
      console.error("Error adding event:", error);
      setAddError("Error adding event");
      setTimeout(() => setAddError(""), 5000);
    }
  };

  const handleDelete = (id) => {
    setDeleteEventId(id);
    setShowDeletePopup(true);
  };

  const performDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/event/delete/${deleteEventId}`);
      setEvents((prev) => prev.filter((event) => event.id !== deleteEventId));
      setEventCount((prev) => Math.max(0, prev - 1));
      setShowDeletePopup(false);
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    const eventDate = new Date(date);
    const year = eventDate.getFullYear();
    const month = (eventDate.getMonth() + 1).toString().padStart(2, "0");
    const day = eventDate.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateToReadableString = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const handleUpdate = (event) => {
    setSelectedEventId(event.id);
    setUpdateEvent({
      title: event.title || "",
      title_ar: event.title_ar || "",
      date: formatDateToYYYYMMDD(event.date),
      content: event.content || "",
      content_ar: event.content_ar || "",
      main_image_en: event.main_image_en || event.main_image || "",
      main_media_type_en: event.main_media_type_en || event.main_media_type || "",
      main_image_ar: event.main_image_ar || event.main_image || "",
      main_media_type_ar: event.main_media_type_ar || event.main_media_type || "",
      additional_media_en: Array.isArray(event.additional_media_en) ? event.additional_media_en : [],
      additional_media_ar: Array.isArray(event.additional_media_ar) ? event.additional_media_ar : [],
    });
    setUpdateMainMediaEn(null);
    setUpdateMainMediaAr(null);
    setUpdateAdditionalMediaEn([]);
    setUpdateAdditionalMediaAr([]);
    setUpdateError("");
    setUpdateSuccessMessage("");
    setRequiredError("");
    setShowUpdateForm(true);
  };

  const handleEventUpdate = async (e) => {
    e.preventDefault();
    setUpdateError("");
    setRequiredError("");

    if (
      !updateEvent.title ||
      !updateEvent.title_ar ||
      !updateEvent.date ||
      !updateEvent.content ||
      !updateEvent.content_ar
    ) {
      setRequiredError("Title, Arabic title, date, and content fields are required.");
      return;
    }

    if (
      (updateMainMediaEn && !isValidMediaExtension(updateMainMediaEn.name)) ||
      (updateMainMediaAr && !isValidMediaExtension(updateMainMediaAr.name))
    ) {
      setUpdateError("Invalid main media file extension. Please upload image/video formats.");
      return;
    }

    if (!validateMediaFiles(updateAdditionalMediaEn) || !validateMediaFiles(updateAdditionalMediaAr)) {
      setUpdateError("Invalid file extension in additional media. Please upload image/video formats.");
      return;
    }

    const shouldReplaceAdditional =
      updateAdditionalMediaEn.length > 0 || updateAdditionalMediaAr.length > 0;

    if (shouldReplaceAdditional && updateAdditionalMediaEn.length !== updateAdditionalMediaAr.length) {
      setUpdateError("English and Arabic additional media must have the same count.");
      return;
    }

    const formData = new FormData();
    formData.append("title", updateEvent.title);
    formData.append("title_ar", updateEvent.title_ar);
    formData.append("date", updateEvent.date);
    formData.append("content", updateEvent.content);
    formData.append("content_ar", updateEvent.content_ar);
    if (updateMainMediaEn) {
      formData.append("main_image_en", updateMainMediaEn);
    }
    if (updateMainMediaAr) {
      formData.append("main_image_ar", updateMainMediaAr);
    }
    updateAdditionalMediaEn.forEach((file) => formData.append("image_paths_en", file));
    updateAdditionalMediaAr.forEach((file) => formData.append("image_paths_ar", file));
    if (!shouldReplaceAdditional) {
      formData.append(
        "media_order",
        JSON.stringify((updateEvent.additional_media_en || []).map((item) => item.id))
      );
    }

    try {
      await axios.put(`${BASE_URL}/event/update/${selectedEventId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchData();
      setUpdateSuccessMessage("Event updated successfully");
      setTimeout(() => setUpdateSuccessMessage(""), 5000);
      setShowUpdateForm(false);
      setSelectedEventId(null);
      setUpdateEvent({
        ...createEmptyEventForm(),
        main_image_en: "",
        main_media_type_en: "",
        main_image_ar: "",
        main_media_type_ar: "",
        additional_media_en: [],
        additional_media_ar: [],
      });
      setUpdateMainMediaEn(null);
      setUpdateMainMediaAr(null);
      setUpdateAdditionalMediaEn([]);
      setUpdateAdditionalMediaAr([]);
    } catch (error) {
      console.error("Error updating event:", error);
      setUpdateError("Error updating event");
      setTimeout(() => setUpdateError(""), 5000);
    }
  };

  return (
    <>
      <div className="content">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md="6">
              <FormGroup>
                <label>Title:</label>
                <Input type="text" name="title" value={newEvent.title} onChange={handleInputChange} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>Arabic Title:</label>
                <Input type="text" name="title_ar" value={newEvent.title_ar} onChange={handleInputChange} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>Date:</label>
                <Input type="date" name="date" value={newEvent.date} onChange={handleInputChange} />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="7">
              <FormGroup>
                <label htmlFor="content" className="textarea-label">Content:</label>
                <textarea
                  className="form-control"
                  id="content"
                  name="content"
                  value={newEvent.content}
                  onChange={handleInputChange}
                  style={{ minHeight: "300px" }}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="7">
              <FormGroup>
                <label htmlFor="content_ar" className="textarea-label">Arabic Content:</label>
                <textarea
                  className="form-control"
                  id="content_ar"
                  name="content_ar"
                  value={newEvent.content_ar}
                  onChange={handleInputChange}
                  style={{ minHeight: "300px" }}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <label>Main Media (EN):</label>
                <Input id="newEventMainMediaEn" type="file" onChange={(e) => setNewMainMediaEn(e.target.files?.[0] || null)} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>Main Media (AR):</label>
                <Input id="newEventMainMediaAr" type="file" onChange={(e) => setNewMainMediaAr(e.target.files?.[0] || null)} />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <label>Additional Media (EN):</label>
                <Input id="newEventAdditionalMediaEn" type="file" multiple onChange={(e) => setNewAdditionalMediaEn(Array.from(e.target.files || []))} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>Additional Media (AR):</label>
                <Input id="newEventAdditionalMediaAr" type="file" multiple onChange={(e) => setNewAdditionalMediaAr(Array.from(e.target.files || []))} />
              </FormGroup>
            </Col>
          </Row>

          <MediaOrderEditor
            title="Additional Media Order (main media always appears first)"
            englishItems={newAdditionalMediaEn}
            arabicItems={newAdditionalMediaAr}
            onMove={moveNewMediaPair}
          />

          <Row>
            <Col md="12">
              <div className="update ml-auto mr-auto">
                <Button className="btn-round" color="primary" type="submit">
                  Add Event
                </Button>
                {addSuccessMessage && (
                  <div className="alert alert-success" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                    {addSuccessMessage}
                  </div>
                )}
                {requiredError && !showUpdateForm && (
                  <div className="alert alert-danger" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                    {requiredError}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Form>

        {addError && <div style={{ color: "red", marginBottom: "10px" }}>{addError}</div>}

        <CardFooter>
          <div>Total Events: {eventCount}</div>
        </CardFooter>

        <div className="content">
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <CardTitle tag="h4">Event List</CardTitle>
                </CardHeader>
                <CardBody>
                  <ItemOrderMessage message={orderMessage} />
                  <Table responsive>
                    <thead className="text-primary">
                      <tr>
                        <th>Title</th>
                        <th>Arabic Title</th>
                        <th>Date</th>
                        <th>Content</th>
                        <th>Arabic Content</th>
                        <th>Main Media (EN)</th>
                        <th>Main Media (AR)</th>
                        <th>Additional Media (EN)</th>
                        <th>Additional Media (AR)</th>
                        <th>Order</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event, index) => (
                        <tr key={event.id}>
                          <td>{event.title}</td>
                          <td>{event.title_ar}</td>
                          <td>{formatDateToReadableString(event.date)}</td>
                          <td>{event.content}</td>
                          <td>{event.content_ar}</td>
                          <td>
                            {renderMediaPreview({
                              path: event.main_image_en || event.main_image,
                              media_type: event.main_media_type_en || event.main_media_type,
                            }, `main-en-${event.id}`)}
                          </td>
                          <td>
                            {renderMediaPreview({
                              path: event.main_image_ar || event.main_image,
                              media_type: event.main_media_type_ar || event.main_media_type,
                            }, `main-ar-${event.id}`)}
                          </td>
                          <td>
                            {Array.isArray(event.additional_media_en) && event.additional_media_en.map((item, index) =>
                              renderMediaPreview(item, `additional-en-${event.id}-${index}`)
                            )}
                          </td>
                          <td>
                            {Array.isArray(event.additional_media_ar) && event.additional_media_ar.map((item, index) =>
                              renderMediaPreview(item, `additional-ar-${event.id}-${index}`)
                            )}
                          </td>
                          <td><ItemOrderControls index={index} count={events.length} onMove={moveItem} disabled={savingOrder} /></td>
                          <td>
                            <Button color="success" onClick={() => handleUpdate(event)} style={{ marginRight: "8px" }}>
                              Update
                            </Button>
                            <Button color="danger" onClick={() => handleDelete(event.id)}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {showUpdateForm && (
        <div className="content">
          <Form onSubmit={handleEventUpdate}>
            <Row>
              <Col md="7">
                <FormGroup>
                  <label>Title:</label>
                  <Input type="text" name="title" id="edit-event-title" value={updateEvent.title} onChange={handleInputChange} />
                </FormGroup>
              </Col>
              <Col md="7">
                <FormGroup>
                  <label>Arabic Title:</label>
                  <Input type="text" name="title_ar" value={updateEvent.title_ar} onChange={handleInputChange} />
                </FormGroup>
              </Col>
              <Col md="7">
                <FormGroup>
                  <label>Date:</label>
                  <Input type="date" name="date" value={updateEvent.date} onChange={handleInputChange} />
                </FormGroup>
              </Col>
            </Row>

            <Col md="7">
              <FormGroup>
                <label htmlFor="edit-content" className="textarea-label">Content:</label>
                <textarea
                  className="form-control"
                  id="edit-content"
                  name="content"
                  value={updateEvent.content}
                  onChange={handleInputChange}
                  style={{ minHeight: "300px", width: "695px" }}
                />
              </FormGroup>
            </Col>
            <Col md="7">
              <FormGroup>
                <label htmlFor="edit-content-ar" className="textarea-label">Arabic Content:</label>
                <textarea
                  className="form-control"
                  id="edit-content-ar"
                  name="content_ar"
                  value={updateEvent.content_ar}
                  onChange={handleInputChange}
                  style={{ minHeight: "300px", width: "695px" }}
                />
              </FormGroup>
            </Col>

            <Row>
              <Col md="6">
                <FormGroup>
                  <label>Current Main Media (EN):</label>
                  <div>
                    {renderMediaPreview({
                      path: updateEvent.main_image_en,
                      media_type: updateEvent.main_media_type_en,
                    }, "update-main-en")}
                  </div>
                  <label>New Main Media (EN) (optional):</label>
                  <Input type="file" onChange={(e) => setUpdateMainMediaEn(e.target.files?.[0] || null)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label>Current Main Media (AR):</label>
                  <div>
                    {renderMediaPreview({
                      path: updateEvent.main_image_ar,
                      media_type: updateEvent.main_media_type_ar,
                    }, "update-main-ar")}
                  </div>
                  <label>New Main Media (AR) (optional):</label>
                  <Input type="file" onChange={(e) => setUpdateMainMediaAr(e.target.files?.[0] || null)} />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <label>Current Additional Media (EN):</label>
                  <div>
                    {Array.isArray(updateEvent.additional_media_en) && updateEvent.additional_media_en.map((item, index) =>
                      renderMediaPreview(item, `update-additional-en-${index}`)
                    )}
                  </div>
                  <label>New Additional Media (EN) (optional):</label>
                  <Input type="file" multiple onChange={(e) => setUpdateAdditionalMediaEn(Array.from(e.target.files || []))} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label>Current Additional Media (AR):</label>
                  <div>
                    {Array.isArray(updateEvent.additional_media_ar) && updateEvent.additional_media_ar.map((item, index) =>
                      renderMediaPreview(item, `update-additional-ar-${index}`)
                    )}
                  </div>
                  <label>New Additional Media (AR) (optional):</label>
                  <Input type="file" multiple onChange={(e) => setUpdateAdditionalMediaAr(Array.from(e.target.files || []))} />
                </FormGroup>
              </Col>
            </Row>

            {updateAdditionalMediaEn.length === 0 && updateAdditionalMediaAr.length === 0 && (
              <MediaOrderEditor
                title="Current Additional Media Order"
                englishItems={updateEvent.additional_media_en}
                arabicItems={updateEvent.additional_media_ar}
                onMove={moveExistingMediaPair}
              />
            )}

            {(updateAdditionalMediaEn.length > 0 || updateAdditionalMediaAr.length > 0) && (
              <MediaOrderEditor
                title="Replacement Media Order (will replace current additional media)"
                englishItems={updateAdditionalMediaEn}
                arabicItems={updateAdditionalMediaAr}
                onMove={moveReplacementMediaPair}
              />
            )}

            <Row>
              <Col md="12">
                <div className="update ml-auto mr-auto">
                  <Button className="btn-round" color="primary" type="submit">
                    Update Event
                  </Button>{" "}
                  <Button
                    className="btn-round"
                    color="secondary"
                    onClick={() => {
                      setShowUpdateForm(false);
                      setSelectedEventId(null);
                      setUpdateEvent({
                        ...createEmptyEventForm(),
                        main_image_en: "",
                        main_media_type_en: "",
                        main_image_ar: "",
                        main_media_type_ar: "",
                        additional_media_en: [],
                        additional_media_ar: [],
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  {updateError && <div style={{ color: "red", marginTop: "10px" }}>{updateError}</div>}
                  {updateSuccessMessage && (
                    <div className="alert alert-success" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                      {updateSuccessMessage}
                    </div>
                  )}
                  {requiredError && (
                    <div className="alert alert-danger" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                      {requiredError}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      )}

      <Modal isOpen={showDeletePopup} toggle={() => setShowDeletePopup(false)}>
        <ModalHeader toggle={() => setShowDeletePopup(false)}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this event?</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={performDelete}>Delete</Button>{" "}
          <Button color="secondary" onClick={() => setShowDeletePopup(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default Events;
